import * as React from 'react';
import { Button } from 'common/components/Form/Button';
import { Text } from 'common/styledComponents/typography';
import TextInput from 'common/components/TextInput';
import { accountDetailsSelector } from 'common/state/account';
import { useRecoilValueLoadable } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useCSVStudyExport } from 'common/components/CSVExportModal/useCSVStudyExport';
import { CSVExportStatus } from 'common/types/common';
import { useEffect, useMemo, useRef } from 'react';
import Modal from 'common/components/Modal';
import { useGetAllStudies } from 'common/queries/studies';
import { LoadingContainer } from 'common/styledComponents/containers';
import Spinner from 'common/components/Spinner';
import { useStudyWaves } from 'common/hooks/waves';
import { normalizeCSVExportData } from 'utils/export';
import { LatanaText } from 'common/components/LatanaText';
import schema from 'common/components/CSVExportModal/CSVExportModal.schema';
import { CheckboxListGroupWrapper, Container, ErrorsContainer, FooterActionsWrapper, Form, P, SelectAllWrapper } from './styled';
import { CSVExportFormItem, FormValues } from 'common/types/export';
import { Study } from 'common/types/account';
import { DataTestId } from 'settings/constants';
import CheckboxListItem from 'common/components/CheckboxListItem';
import DropdownPillMenu from 'common/components/DropdownPillMenu';

interface Props {
  onClose: () => void;
  open: boolean;
}

export const Fallback: React.FC = () => (
  <LoadingContainer>
    <Spinner />
  </LoadingContainer>
);

type ResetCSVExportForm = (values: FormValues) => void;

const getSortedUniqueValues = (values: string[]): string[] => Array.from(new Set(values)).sort();

const getWaveItems = (wavesToStudyMap: Map<string, CSVExportFormItem[]>): CSVExportFormItem[] =>
  Array.from(wavesToStudyMap.values()).flat();

const buildCSVExportFormKey = (accountEmail: string, studyList: Study[], waveItems: CSVExportFormItem[]): string => {
  const studyIds = getSortedUniqueValues(studyList.map(({ uuid }) => uuid));
  const waveIds = getSortedUniqueValues(waveItems.map(({ value }) => value));

  return `${accountEmail}|${studyIds.join(',')}|${waveIds.join(',')}`;
};

const buildCSVExportDefaultValues = (accountEmail: string, studyList: Study[], waveItems: CSVExportFormItem[]): FormValues => {
  const studyEntries = studyList.map(({ uuid }) => [uuid, true]);
  const allWaveEntries = waveItems.map(({ value }) => [`wave_${value}`, true]);

  return {
    email: accountEmail,
    studies: Object.fromEntries(studyEntries),
    waves: Object.fromEntries(allWaveEntries),
  };
};

const useInitCSVExportForm = ({
  open,
  accountEmail,
  studyList,
  wavesToStudyMap,
  reset,
}: {
  open: boolean;
  accountEmail?: string;
  studyList?: Study[];
  wavesToStudyMap: Map<string, CSVExportFormItem[]>;
  reset: ResetCSVExportForm;
}) => {
  const initializedFormKeyRef = useRef<string>();

  useEffect(() => {
    if (!open || !accountEmail || !studyList || studyList.length === 0 || wavesToStudyMap.size === 0) return;

    const waveItems = getWaveItems(wavesToStudyMap);
    const formKey = buildCSVExportFormKey(accountEmail, studyList, waveItems);

    if (initializedFormKeyRef.current === formKey) return;

    reset(buildCSVExportDefaultValues(accountEmail, studyList, waveItems));
    initializedFormKeyRef.current = formKey;
  }, [open, studyList, wavesToStudyMap, accountEmail, reset]);

  useEffect(() => {
    if (!open) {
      initializedFormKeyRef.current = undefined;
    }
  }, [open]);
};

const CSVExportModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();

  const accountDetails = useRecoilValueLoadable(accountDetailsSelector);

  const { exportCSV, csvExportStatus, setCSVExportStatus } = useCSVStudyExport();
  const { data: studyList, isLoading: isStudiesListLoading } = useGetAllStudies();
  const { waves: wavesToStudyMap, isLoading: isWavesToStudyMapLoading } = useStudyWaves(studyList);

  const { register, reset, handleSubmit, errors, control, setValue, getValues, watch } = useForm<FormValues>({
    defaultValues: {
      waves: {},
      email: '',
      studies: {},
    },
    shouldUnregister: false,
    resolver: yupResolver(schema),
  });

  const studiesFormList = useMemo<CSVExportFormItem[]>(() => {
    return studyList?.map(({ name, uuid }) => ({ label: name, value: uuid })) || [];
  }, [studyList]);

  const accountEmail = accountDetails.state === 'hasValue' ? accountDetails.contents.user?.email : undefined;

  useInitCSVExportForm({ open, accountEmail, studyList, wavesToStudyMap, reset });

  const handleClose = React.useCallback(() => {
    onClose();
    setTimeout(() => setCSVExportStatus(CSVExportStatus.PENDING), 400);
  }, [onClose, setCSVExportStatus]);

  const validateFormSelection = (formValues: { [x: string]: boolean }) => {
    if (!formValues) return false;
    return Object.values(formValues).every(value => !!value);
  };

  const formValues = watch();

  const isAllStudiesSelected = React.useMemo(() => {
    return validateFormSelection(formValues.studies);
  }, [formValues.studies]);

  const toggleAllSelection = () => {
    const { studies, waves } = getValues();
    const isSelected = !isAllStudiesSelected;

    for (const study in studies) {
      setValue(`studies.${study}`, isSelected);
    }

    for (const wave in waves) {
      setValue(`waves.${wave}`, isSelected);
    }
  };

  const getStudyRelatedWavesId = (studyId: string): string[] => {
    const wavesList = wavesToStudyMap.get(studyId);
    if (!wavesList?.length) {
      return [];
    }
    return wavesList.map(({ value }) => value);
  };

  const handleStudyToggle = (studyId: string, isChecked: boolean) => {
    setValue(`studies.${studyId}`, isChecked);

    const currentValues = getValues();
    const studyRelatedWavesId = getStudyRelatedWavesId(studyId);

    /** Ensure `waves` object exists before mutating it. */
    if (!currentValues.waves) {
      setValue('waves', {});
    }

    /**
     * If study is selected - select all related waves
     * If study isn't selected - deselect all related waves
     * */
    studyRelatedWavesId.forEach(waveId => {
      setValue(`waves.wave_${waveId}`, isChecked);
    });
  };

  const handleWaveToggle = (studyId: string, waveId: string, isChecked: boolean) => {
    setValue(`waves.wave_${waveId}`, isChecked);

    if (isChecked) {
      /** If any wave selected - related study must be selected */
      setValue(`studies.${studyId}`, true);
    }

    if (!isChecked) {
      const wavesFormValues = getValues();
      const studyRelatedWavesId = getStudyRelatedWavesId(studyId);
      const isNoWavesSelected = studyRelatedWavesId.every(waveId => !wavesFormValues.waves[`wave_${waveId}`]);

      /** If no waves selected for a specific study - deselect this study */
      if (isNoWavesSelected) {
        setValue(`studies.${studyId}`, false);
      }
    }
  };

  if (!studiesFormList.length || !accountEmail || isStudiesListLoading || !wavesToStudyMap.size || isWavesToStudyMapLoading) {
    return <Fallback />;
  }

  return (
    <Modal
      isOpen={open}
      customWidth={600}
      titleAlign='left'
      customSpacing={[3]}
      handleClose={onClose}
      showCloseButton={false}
      clickOutsideClose={true}
      title={t('csvExport.exportCSV')}
    >
      <Container>
        {csvExportStatus === CSVExportStatus.SUCCESS ? (
          <React.Fragment>
            <P variant='h5'>{t('csvExport.success')}</P>
            <FooterActionsWrapper>
              <Button onClick={handleClose} type='button' pseudo opacityHover>
                {t('common.gotIt')}
              </Button>
            </FooterActionsWrapper>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <SelectAllWrapper onClick={toggleAllSelection} data-testid={DataTestId.CSV_EXPORT_MODAL_DESELECT_ALL}>
              <Text variant='body2' color='lightPurple'>
                {isAllStudiesSelected ? t('csvExport.deselectAll') : t('csvExport.selectAll')}
              </Text>
            </SelectAllWrapper>
            <CheckboxListGroupWrapper>
              {studiesFormList.map(({ value: studyId, label }) => {
                return (
                  <Controller
                    key={`study-${studyId}`}
                    name={`studies.${studyId}`}
                    control={control}
                    render={field => (
                      <React.Suspense fallback={<Fallback />}>
                        <CheckboxListItem
                          spaceBetween
                          label={label}
                          value={studyId}
                          checked={field.value || false}
                          onCheck={(studyId, checked) => handleStudyToggle(studyId, checked)}
                          renderChild={() => (
                            <React.Suspense fallback={<Fallback />}>
                              <DropdownPillMenu
                                noHover
                                noPadding
                                multiselect
                                popupWidth={434}
                                id={`study-waves-item-${studyId}`}
                                items={wavesToStudyMap.get(studyId) || []}
                                popupContainerPadding={[1, 3, 3]}
                                renderPopupHeader={() => (
                                  <SelectAllWrapper>
                                    <LatanaText variant='L1' color='gray900'>
                                      {t('csvExport.selectWaves')}
                                    </LatanaText>
                                  </SelectAllWrapper>
                                )}
                                renderSelectItem={(waveId, _, label) => (
                                  <Controller
                                    control={control}
                                    name={`waves.wave_${waveId}`}
                                    render={waveField => (
                                      <CheckboxListItem
                                        reverseOrder
                                        value={waveId}
                                        label={label as string}
                                        checked={waveField.value || false}
                                        onCheck={(waveId, checked) => handleWaveToggle(studyId, waveId, checked)}
                                      />
                                    )}
                                  />
                                )}
                              />
                            </React.Suspense>
                          )}
                        />
                      </React.Suspense>
                    )}
                  />
                );
              })}
            </CheckboxListGroupWrapper>

            <P variant='body2' color='sooty'>
              {t('csvExport.desc')}
            </P>

            <Form
              aria-busy={csvExportStatus === CSVExportStatus.LOADING}
              onSubmit={handleSubmit(formData => exportCSV(normalizeCSVExportData(formData)))}
            >
              <TextInput
                ref={register({
                  required: true,
                })}
                placeholder='Email'
                type='email'
                marginBottom={false}
                variant='smaller'
                name='email'
              />
              {errors && (
                <ErrorsContainer>
                  {Object.entries(errors).map(([key, error]) => (
                    <Text key={key} color='redOrange' variant='body2'>
                      {error?.message}
                    </Text>
                  ))}
                </ErrorsContainer>
              )}
              <FooterActionsWrapper>
                <Button onClick={handleClose} type='button' pseudo opacityHover>
                  {t('common.cancel')}
                </Button>
                <Button disabled={Boolean(errors.email)} type='submit' pill={false} square>
                  {t('csvExport.submit')}
                </Button>
              </FooterActionsWrapper>
            </Form>
          </React.Fragment>
        )}
      </Container>
    </Modal>
  );
};

export default React.memo(CSVExportModal);
